/**
 * Per-game session persistence in localStorage, keyed by the URL sessionId.
 *
 * Every game (local / bot / online — signed in or not) saves enough state to be rebuilt on
 * reload of /g/<id>. We store:
 *   - mode + humanSide + victoryConfig (the "config" of the game)
 *   - the serialized GameState (board, captured, history, victory, etc.)
 *   - for online: the joinCode + rejoinToken + mySide so we can reconnect via the worker
 *
 * NOT persisted:
 *   - tutorial mode (scripted, no need to restore)
 *   - in-flight `pending` capture choice (rare; restoring requires re-running applyMove to
 *     rebuild the declarable list, which we just skip — the user reselects)
 *   - animations / selected / destinations (transient UI state)
 *   - the live NetClient or sockets (re-created via reconnect for online)
 */

import { toWire, fromWire } from "../../worker/src/serialize";
import {
  applyTurn,
  createInitialState,
  defaultVictoryConfig,
  type GameState,
  type Side,
  type VictoryConfig,
  type VictoryLevel,
} from "../engine";
import type { WireGameState } from "../../worker/src/protocol";
import * as gamesApi from "../games/api";
import { loadCachedUser } from "../auth/storage";

const STORAGE_PREFIX = "rithmo/g/";
const SCHEMA_VERSION = 1;

export type PersistedMode = "local" | "bot" | "online";

export interface PersistedOnlineMeta {
  code: string;
  mySide: Side;
  rejoinToken: string;
}

export interface SavedSession {
  v: number;
  sessionId: string;
  mode: PersistedMode;
  humanSide: Side;
  victoryConfig: VictoryConfig;
  state: WireGameState;
  online?: PersistedOnlineMeta;
  updatedAt: number;
}

function storageKey(sessionId: string): string {
  return `${STORAGE_PREFIX}${sessionId}`;
}

export function saveSession(input: {
  sessionId: string;
  mode: PersistedMode;
  humanSide: Side;
  victoryConfig: VictoryConfig;
  state: GameState;
  online?: PersistedOnlineMeta;
}): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  const payload: SavedSession = {
    v: SCHEMA_VERSION,
    sessionId: input.sessionId,
    mode: input.mode,
    humanSide: input.humanSide,
    victoryConfig: input.victoryConfig,
    state: toWire(input.state),
    online: input.online,
    updatedAt: Date.now(),
  };
  try {
    window.localStorage.setItem(storageKey(input.sessionId), JSON.stringify(payload));
  } catch {
    // QuotaExceeded or sandboxed; not fatal — the game still runs, it just won't survive reload.
  }
}

export function loadSession(sessionId: string): (Omit<SavedSession, "state"> & { state: GameState }) | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(storageKey(sessionId));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SavedSession;
    if (parsed.v !== SCHEMA_VERSION) return null;
    if (!parsed.state || !parsed.mode) return null;
    return { ...parsed, state: fromWire(parsed.state) };
  } catch {
    return null;
  }
}

export function clearSession(sessionId: string): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(storageKey(sessionId));
  } catch {
    /* noop */
  }
}

/** Outcome of an attempted server-side restore. */
export type ServerRestoreResult =
  | { kind: "ok"; data: Omit<SavedSession, "state"> & { state: GameState } }
  | { kind: "not-found" } // not signed in, not your game, or unknown id
  | { kind: "online-unsupported" }; // online cross-device restore isn't wired up yet

/**
 * Fetch a signed-in user's game record from D1, replay the move log, and return the same
 * shape as loadSession(). Used as a cross-device fallback when /g/<id> hits a localStorage
 * miss — the API returns not-found for unauthenticated users, games they don't own, or
 * unknown ids. Online games are returned as online-unsupported (we have the state but no
 * device-local rejoinToken to reconnect with), so the UI can show a useful error.
 */
export async function loadSessionFromServer(gameId: string): Promise<ServerRestoreResult> {
  const me = loadCachedUser();
  if (!me) return { kind: "not-found" };

  const result = await gamesApi.getGameWithMoves(gameId);
  if (!result) return { kind: "not-found" };

  const { game, moves } = result;
  if (game.mode === "online") return { kind: "online-unsupported" };
  if (game.mode !== "bot") return { kind: "not-found" };

  const humanSide: Side = game.whiteUserId === me.id ? "white" : game.blackUserId === me.id ? "black" : "white";
  // Base on the engine's current defaults so future fields (properMinPieces/Shapes, etc.)
  // get sensible values. Override only what D1 actually persists per row.
  const victoryConfig: VictoryConfig = {
    ...defaultVictoryConfig(),
    level: game.victoryLevel as VictoryLevel,
    ...(game.threshold !== null ? { commonThreshold: game.threshold } : {}),
  };

  let state = createInitialState(victoryConfig);
  for (const move of moves) {
    try {
      const r = applyTurn(state, move.move, {
        declared: move.declared ?? undefined,
        advance: move.advance,
      });
      state = r.state;
    } catch {
      // Replay failure (out-of-date move format, etc.) — bail and let the UI show menu.
      return { kind: "not-found" };
    }
  }

  return {
    kind: "ok",
    data: {
      v: SCHEMA_VERSION,
      sessionId: gameId,
      mode: "bot",
      humanSide,
      victoryConfig,
      state,
      online: undefined,
      updatedAt: Date.now(),
    },
  };
}
